import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Alert,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Pagination,
  Link,
  Tab,
  Badge,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress, 
  FormControlLabel, 
  FormGroup, 
  Checkbox
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { lessonsAPI } from '../services/api';
import Button from '../components/UIKit/Button';
import Card from '../components/UIKit/Card';
import Tabs from '../components/UIKit/Tabs';
import Spinner from '../components/UIKit/Spinner';
import TextField from '../components/UIKit/TextField';
import LessonEditor from '../components/UIKit/LessonEditor';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { formatDate } from '../utils/formatters';

// Получение цвета чипа статуса
function getStatusChipColor(status) {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
}

// Получение иконки статуса
function getStatusIcon(status) {
  switch (status) {
    case 'pending':
      return <AccessTimeIcon />;
    case 'approved':
      return <CheckCircleIcon />;
    case 'rejected':
      return <CancelIcon />;
    default:
      return null;
  }
}

const MyLessonChangeRequestsPage = () => {
  const theme = useTheme();
  const { t } = useTranslation(['lessons', 'common']);
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [changeRequests, setChangeRequests] = useState([]);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [totalCount, setTotalCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState('all');
  const [counts, setCounts] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [selectedCommentTitle, setSelectedCommentTitle] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editRequest, setEditRequest] = useState(null);
  const navigate = useNavigate();

  // Загрузка данных запросов на изменение
  const fetchChangeRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page,
        limit: limit,
        status: filterStatus === 'all' ? undefined : filterStatus,
      };

      const response = await lessonsAPI.getUserChangeRequests(params);

      if (response && response.data) {
        setChangeRequests(response.data.data);
        setTotalCount(response.data.pagination.total);

        // Обновляем счетчики
        if (response.data.counts) {
          setCounts(response.data.counts);
        }
      } else {
        setError(t('lessons:changeRequests.errors.loadError'));
      }
    } catch (err) {
      console.error('Error fetching change requests:', err);
      setError(t('lessons:changeRequests.errors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, limit, filterStatus, t]);

  // Проверка, является ли пользователь админом
  const isAdmin = useMemo(() => user && user.userMeta.roles.includes('admin'), [user]);

  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        fetchChangeRequests();
      } else {
        setLoading(false);
      }
    }
  }, [fetchChangeRequests, user, isAdmin]);

  // Обработчик пагинации
  const handleChangePage = useCallback((event, value) => {
    setPage(value);
  }, []);

  // Обработчик изменения фильтра по статусу
  const handleChangeTab = useCallback((event, newValue) => {
    setFilterStatus(newValue);
    setPage(1);
  }, []);

  // Открытие модального окна для просмотра комментария
  const handleViewComment = useCallback((comment, title) => {
    setSelectedComment(comment);
    setSelectedCommentTitle(title);
    setCommentModalOpen(true);
  }, []);

  // Закрытие модального окна комментария
  const handleCloseCommentModal = useCallback(() => {
    setCommentModalOpen(false);
    setSelectedComment(null);
    setSelectedCommentTitle('');
  }, []);

  // Обработчики для меню действий
  const handleMenuOpen = useCallback((event, request) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedRequest(null);
  }, []);

  // Обработчик редактирования запроса
  const handleEditRequest = useCallback(() => {
    if (selectedRequest) {
      const req = selectedRequest;
      handleMenuClose();
      const lessonSlug = req.lesson.slug || req.lesson._id;
      navigate(`/request-lesson-changes/${lessonSlug}?requestId=${req._id}`);
    }
  }, [selectedRequest, handleMenuClose, navigate]);

  // Обработчик удаления запроса
  const handleDeleteRequest = useCallback(() => {
    setDeleteTarget(selectedRequest);
    handleMenuClose();
    setDeleteDialogOpen(true);
  }, [handleMenuClose, selectedRequest]);

  // Подтверждение удаления
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;

    try {
      setDeleting(true);
      await lessonsAPI.cancelChangeRequest(deleteTarget._id);
      // Optimistically update list
      setChangeRequests(prev => prev.filter(r => r._id !== deleteTarget._id));
      setTotalCount(prev => Math.max(0, prev - 1));
      if (deleteTarget.status && counts[deleteTarget.status] !== undefined) {
        setCounts(prev => ({ ...prev, [deleteTarget.status]: Math.max(0, (prev[deleteTarget.status] || 1) - 1) }));
      }
      toast.success(t('lessons:changeRequests.messages.deleteSuccess'));
      setDeleteDialogOpen(false);
      setSelectedRequest(null);
      setDeleteTarget(null);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(
        error.response?.data?.message || t('lessons:changeRequests.errors.deleteError')
      );
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, t, counts]);

  const handleEditDialogClose = useCallback(() => {
    setEditDialogOpen(false);
    setEditRequest(null);
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!editRequest?._id) return;
    try {
      setEditSaving(true);
      const { _id, comment, proposedChanges, selectedBlocks } = editRequest;
      await lessonsAPI.updateMyChangeRequest(_id, { comment, proposedChanges, selectedBlocks });
      // Update item in list state without reload
      setChangeRequests(prev => prev.map(r => r._id === _id ? { ...r, comment, proposedChanges, selectedBlocks } : r));
      toast.success(t('lessons:requestChanges.updateSuccess'));
      setEditDialogOpen(false);
      setEditRequest(null);
    } catch (e) {
      toast.error(t('common:errors.savingFailed'));
    } finally {
      setEditSaving(false);
    }
  }, [editRequest, t]);

  // Закрытие диалога удаления
  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setSelectedRequest(null);
    setDeleteTarget(null);
  }, []);

  // Проверяем доступ - только для не-администраторов
  if (user && isAdmin) {
    return (
      <Container sx={{ mt: 4, px: isMobile ? 1 : 3 }}>
        <Box sx={{ textAlign: 'center', my: 5 }}>
          <Typography color="error" variant="h6">
            {t('common:errors.accessDenied')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('lessons:changeRequests.accessDeniedMessage')}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ mt: 4, px: isMobile ? 1 : 3 }}>
      {/* Фильтры */}
      <Grid
        container
        spacing={{ xs: 1, lg: 2 }}
        sx={{
          mb: 3,
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
        }}
      >
        <Grid item xs={12} lg>
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            component="h1"
            display="flex"
            alignItems="center"
            gap={1}
          >
            <AssignmentIcon sx={{ fontSize: isMobile ? '1.8rem' : '2.2rem' }} />
            {t('lessons:changeRequests.myRequestsTitle')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          lg={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: { xs: '12px', sm: '16px' },
              p: '4px',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <Tabs
              value={filterStatus}
              onChange={handleChangeTab}
              swapBackground
            >
              <Tab
                label={t('lessons:changeRequests.tabs.all')}
                value="all"
              />
              <Tab
                label={
                  <Badge badgeContent={counts.pending} color="warning">
                    {t('lessons:changeRequests.tabs.pending')}
                  </Badge>
                }
                value="pending"
              />
              <Tab
                label={
                  <Badge badgeContent={counts.approved} color="success">
                    {t('lessons:changeRequests.tabs.approved')}
                  </Badge>
                }
                value="approved"
              />
              <Tab
                label={
                  <Badge badgeContent={counts.rejected} color="error">
                    {t('lessons:changeRequests.tabs.rejected')}
                  </Badge>
                }
                value="rejected"
              />
            </Tabs>
          </Box>
        </Grid>
      </Grid>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 4 }}
          action={
            <Button
              variant="text"
              size="small"
              onClick={fetchChangeRequests}
            >
              {t('common:actions.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Загрузка */}
      {loading ? (
        <Spinner />
      ) : error ? (
        <Box sx={{ textAlign: 'center', my: 5 }}>
          <Typography color="error">{error}</Typography>
          <Button variant="contained" onClick={fetchChangeRequests} sx={{ mt: 2 }}>
            {t('common:actions.retry')}
          </Button>
        </Box>
      ) : changeRequests.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <InfoIcon color="info" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            {t('lessons:changeRequests.noRequests')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('lessons:changeRequests.noRequestsDescription')}
          </Typography>
        </Box>
      ) : (
        <>
          <Card sx={{ p: 3 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '35%' }}>
                    {t('lessons:changeRequests.table.lesson')}
                  </TableCell>
                  <TableCell sx={{ width: '20%' }}>
                    {t('lessons:changeRequests.table.date')}
                  </TableCell>
                  <TableCell sx={{ width: '20%' }}>
                    {t('lessons:changeRequests.table.status')}
                  </TableCell>
                  <TableCell sx={{ width: '25%' }} align="center">
                    {t('lessons:changeRequests.table.response')}
                  </TableCell>
                  <TableCell sx={{ width: '60px' }} align="right">
                    {t('lessons:changeRequests.table.actions')}
                  </TableCell>
                </TableRow>
              </TableHead>
            <TableBody>
              {changeRequests.map((request) => (
                <TableRow key={request._id} hover>
                  <TableCell>
                    <Link
                      href={`/lessons/${request.lesson.slug || request.lesson._id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {request.lesson.title}
                    </Link>
                  </TableCell>
                  <TableCell>{formatDate(request.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(request.status)}
                      label={t(
                        `lessons:changeRequests.status.${request.status}`
                      )}
                      color={getStatusChipColor(request.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ width: '60px' }}>
                    {request.adminComment && request.adminComment.trim() ? (
                      <IconButton
                        size="small"
                        onClick={() => handleViewComment(
                          request.adminComment,
                          `Response for "${request.lesson.title}"`
                        )}
                        color="primary"
                      >
                        <InfoIcon sx={{
                          fontSize: '1.2rem',
                        }} />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        disabled
                        sx={{
                          opacity: 0.5,
                          cursor: 'default',
                        }}
                      >
                        <InfoIcon sx={{
                          fontSize: '1.2rem',
                          color: 'text.disabled',
                        }} />
                      </IconButton>
                    )}
                  </TableCell>

                  {/* Actions */}
                 <TableCell align="right" sx={{ width: '60px' }}>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, request)}
                    disabled={request.status !== "pending"}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
                </TableRow>
              ))}
              {loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Пагинация */}
        {totalCount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
            <Pagination
              count={Math.ceil(totalCount / limit)}
              page={page}
              onChange={handleChangePage}
              color="primary"
            />
          </Box>
        )}
        </Card>
        </>
      )}

      {/* Модальное окно для просмотра комментария */}
      <Dialog
        open={commentModalOpen}
        onClose={handleCloseCommentModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {selectedCommentTitle}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleCloseCommentModal}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

       <DialogContent dividers>
  {editLoading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <CircularProgress />
    </Box>
  ) : editRequest ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Назва уроку */}
      <Typography variant="subtitle2">{t('lessons:changeRequests.table.lesson')}</Typography>
      <Typography variant="body1">{editRequest.lesson?.title}</Typography>

      {/* Коментар */}
      <Typography variant="subtitle2">{t('lessons:requestChanges.commentLabel')}</Typography>
      <TextField
        fullWidth
        multiline
        minRows={3}
        value={editRequest.comment}
        onChange={(e) =>
          setEditRequest((prev) => ({ ...prev, comment: e.target.value }))
        }
      />

      {/* Proposed Changes (JSON редагування) */}
      <Typography variant="subtitle2">Changes JSON</Typography>
      <TextField
        fullWidth
        multiline
        minRows={6}
        value={JSON.stringify(editRequest.proposedChanges, null, 2)}
        onChange={(e) => {
          try {
            const parsed = JSON.parse(e.target.value);
            setEditRequest((prev) => ({ ...prev, proposedChanges: parsed }));
          } catch {
            // TODO: показати помилку парсингу
          }
        }}
        sx={{ fontFamily: 'monospace' }}
      />

      {/* Selected Blocks (Checkbox Group) */}
      <Typography variant="subtitle2">Selected Blocks</Typography>
      <FormGroup>
        {["video", "presentation", "html", "slides", "labyrinth"].map((block) => (
          <FormControlLabel
            key={block}
            control={
              <Checkbox
                checked={editRequest.selectedBlocks.includes(block)}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setEditRequest((prev) => ({
                    ...prev,
                    selectedBlocks: checked
                      ? [...prev.selectedBlocks, block]
                      : prev.selectedBlocks.filter((b) => b !== block),
                  }));
                }}
              />
            }
            label={block}
          />
        ))}
      </FormGroup>
    </Box>
  ) : (
    <Alert severity="warning">{t('lessons:changeRequests.noRequests')}</Alert>
  )}
</DialogContent>


        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={handleCloseCommentModal}>
            {t('common:actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          zIndex: 9999,
          '& .MuiPaper-root': {
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: '8px',
          }
        }}
      >
        <MenuItem 
          onClick={() => {
            handleEditRequest();
          }} 
          disabled={false}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common:actions.edit')}</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            handleDeleteRequest();
          }} 
          disabled={false}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>{t('common:actions.delete')}</ListItemText>
        </MenuItem>
      </Menu>
        
      {/* Диалог редактирования запроса */}
      <Dialog open={editDialogOpen} onClose={handleEditDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>{t('common:actions.edit')}</DialogTitle>
        <DialogContent dividers>
          {editLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : editRequest ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Lesson Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('lessons:changeRequests.table.lesson')}
                </Typography>
                <Typography variant="h6">{editRequest.lesson?.title}</Typography>
              </Box>

              {/* Comment */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('lessons:requestChanges.commentLabel')}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={editRequest.comment || ''}
                  onChange={(e) => setEditRequest(prev => ({ ...prev, comment: e.target.value }))}
                  placeholder={t('lessons:requestChanges.commentPlaceholder')}
                />
              </Box>

              {/* Full Lesson Editor */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('lessons:requestChanges.proposedChanges')}
                </Typography>
                
                <LessonEditor
                  lesson={{
                    ...editRequest.lesson,
                    ...editRequest.proposedChanges,
                    // Ensure we have all required fields
                    title: editRequest.proposedChanges?.title || editRequest.lesson?.title || '',
                    contentType: editRequest.proposedChanges?.contentType || editRequest.lesson?.contentType || 'standard',
                    content: editRequest.proposedChanges?.content || editRequest.lesson?.content || '',
                    duration: editRequest.proposedChanges?.duration || editRequest.lesson?.duration || 1,
                    type: editRequest.proposedChanges?.type || editRequest.lesson?.type || 'text',
                    videoUrl: editRequest.proposedChanges?.videoUrl || editRequest.lesson?.videoUrl || '',
                    contentBlocks: editRequest.proposedChanges?.contentBlocks || editRequest.lesson?.contentBlocks || [],
                    resources: editRequest.proposedChanges?.resources || editRequest.lesson?.resources || [],
                    practiceExercises: editRequest.proposedChanges?.practiceExercises || editRequest.lesson?.practiceExercises || [],
                    isDraft: editRequest.lesson?.isDraft || false
                  }}
                  onChange={(updatedLesson) => {
                    setEditRequest(prev => ({
                      ...prev,
                      proposedChanges: {
                        ...prev.proposedChanges,
                        title: updatedLesson.title,
                        contentType: updatedLesson.contentType,
                        content: updatedLesson.content,
                        duration: updatedLesson.duration,
                        type: updatedLesson.type,
                        videoUrl: updatedLesson.videoUrl,
                        contentBlocks: updatedLesson.contentBlocks,
                        resources: updatedLesson.resources,
                        practiceExercises: updatedLesson.practiceExercises
                      }
                    }));
                  }}
                  showTestsTab={false}
                  isAdminOrTeacher={false}
                />
              </Box>
            </Box>
          ) : (
            <Alert severity="warning">{t('lessons:changeRequests.noRequests')}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleEditDialogClose} disabled={editSaving}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving || editLoading}>
            {editSaving ? t('common:actions.saving') : t('common:actions.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{t('common:actions.confirm')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('lessons:changeRequests.confirmDelete', { title: deleteTarget?.lesson?.title || '' })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleCloseDeleteDialog} disabled={deleting}>
            {t('common:actions.cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting}>
            {t('common:actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyLessonChangeRequestsPage;
